import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useFinanceMetrics, calculateYtdTotals, getLastMonthStats } from '@/hooks/useFinanceAnalytics';
import { cn } from '@/lib/utils';

const MONTH_NAMES = ['stycznia', 'lutego', 'marca', 'kwietnia', 'maja', 'czerwca',
                     'lipca', 'sierpnia', 'września', 'października', 'listopada', 'grudnia'];

export default function RevenueSummaryCards({ branchId, year, filterMode }) {
  const { data: monthlyData, isLoading } = useFinanceMetrics(branchId, year);

  const ytdTotals = React.useMemo(() => {
    return calculateYtdTotals(monthlyData);
  }, [monthlyData]);

  const { lastMonth, changePercent } = React.useMemo(() => {
    return getLastMonthStats(monthlyData);
  }, [monthlyData]);

  // Format currency
  const formatCurrency = (cents) => {
    return (cents / 100).toLocaleString('pl-PL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-8 w-40" />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-8 w-40" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* Total Revenue Card */}
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-slate-500 mb-1">Całkowite przychody</p>
          <p className="text-2xl font-bold text-slate-900">
            {formatCurrency(ytdTotals.totalPaid)} PLN
            {ytdTotals.totalBilled > 0 && (
              <span className="text-lg font-normal text-slate-500 ml-1">
                z {formatCurrency(ytdTotals.totalBilled)} PLN
              </span>
            )}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {ytdTotals.paidCount} z {ytdTotals.receiptCount} rachunków opłaconych
          </p>
        </CardContent>
      </Card>

      {/* Last Month Card */}
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-slate-500 mb-1">
            {lastMonth ? `${MONTH_NAMES[lastMonth.month - 1]}` : 'Poprzedni miesiąc'}
          </p>
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-bold text-slate-900">
              {lastMonth
                ? `${formatCurrency(lastMonth.paid_amount_cents)} PLN`
                : '—'
              }
            </p>
            {changePercent !== null && (
              <div className={cn(
                "flex items-center gap-1 text-sm font-medium",
                changePercent > 0 ? "text-emerald-600" :
                changePercent < 0 ? "text-red-600" : "text-slate-400"
              )}>
                {changePercent > 0 ? (
                  <TrendingUp className="w-4 h-4" />
                ) : changePercent < 0 ? (
                  <TrendingDown className="w-4 h-4" />
                ) : (
                  <Minus className="w-4 h-4" />
                )}
                <span>
                  {changePercent > 0 ? '+' : ''}{changePercent.toFixed(1)}%
                </span>
              </div>
            )}
          </div>
          {changePercent !== null && (
            <p className="text-xs text-slate-400 mt-1">
              vs {MONTH_NAMES[(lastMonth?.month || 1) - 2] || 'poprzedni miesiąc'}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
